import { useEffect, useState } from "react";
import { useAnimate } from "framer-motion";
import {
  E_PARTS,
  GLYPHS,
  LAYOUT_ITEMS,
  TOTAL_W,
} from "@/components/Logo";

const SESSION_KEY = "eservice-intro-played";

// How far the standalone "E" starts from the left: the exact x-position of the
// final "E" in "SERVICE" (far right), so it slides all the way left into place
// while revealing "SERVICE" underneath.
const E_OFFSET = LAYOUT_ITEMS[LAYOUT_ITEMS.length - 1].x;
const SERVICE_ITEMS = LAYOUT_ITEMS.slice(1); // S E R V I C E

/**
 * Boot animation for the "E SERVICE" wordmark:
 *   black -> E top fades in -> E middle -> E bottom -> E slides left
 *   -> "SERVICE" fades in -> whole logo fades into the landing page.
 * Fast, minimalistic, and responsive (logo capped so it never gets too big).
 */
const IntroAnimation = () => {
  const [scope, animate] = useAnimate();
  const [mounted, setMounted] = useState(() => {
    if (typeof window === "undefined") return false;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) return false;
    // Tolerant guard: a value older than 3s means it truly played on a prior
    // load; anything fresher is React StrictMode's dev-only remount, so keep going.
    const prev = Number(sessionStorage.getItem(SESSION_KEY) || 0);
    if (prev && Date.now() - prev > 3000) return false;
    sessionStorage.setItem(SESSION_KEY, String(Date.now()));
    return true;
  });

  useEffect(() => {
    if (!mounted) return;
    document.body.style.overflow = "hidden";
    window.scrollTo(0, 0);
    let cancelled = false;
    const step = (sel, props, opts) =>
      cancelled ? Promise.resolve() : animate(sel, props, opts);

    const run = async () => {
      // Hold on a plain black screen first, so the page has time to fully load underneath.
      await new Promise((r) => setTimeout(r, 900));
      if (cancelled) return;
      await step("[data-e='top']", { opacity: 1 }, { duration: 0.15, ease: "easeOut" });
      await step("[data-e='mid']", { opacity: 1 }, { duration: 0.15, ease: "easeOut" });
      await step("[data-e='bot']", { opacity: 1 }, { duration: 0.15, ease: "easeOut" });
      if (cancelled) return;
      await Promise.all([
        animate("[data-part='e-group']", { x: [E_OFFSET, 0] }, { duration: 0.85, ease: [0.16, 1, 0.3, 1] }),
        animate("[data-part='service']", { opacity: [0, 1] }, { duration: 0.75, delay: 0.2, ease: "easeOut" }),
      ]);
      if (cancelled) return;
      await new Promise((r) => setTimeout(r, 400));
      await step("[data-part='logo']", { opacity: 0, filter: "blur(2px)" }, { duration: 0.35, ease: "easeIn" });
      await step(scope.current, { opacity: 0 }, { duration: 0.4, ease: "easeInOut" });
      window.scrollTo(0, 0);
      if (!cancelled) setMounted(false);
    };
    run();

    // Safety net: never let the intro block the page beyond this, even if the
    // animation engine stalls (e.g. tab loses focus mid-intro).
    const failsafe = setTimeout(() => {
      if (!cancelled) {
        cancelled = true;
        document.body.style.overflow = "";
        setMounted(false);
      }
    }, 4000);

    return () => {
      cancelled = true;
      clearTimeout(failsafe);
      document.body.style.overflow = "";
    };
  }, [mounted, animate, scope]);

  if (!mounted) return null;

  return (
    <div
      ref={scope}
      className="fixed inset-0 z-[999] bg-black flex items-center justify-center"
      data-testid="intro-animation"
      aria-hidden="true"
    >
      <div data-part="logo" className="flex items-center justify-center">
        <svg
          viewBox={`0 0 ${TOTAL_W} 100`}
          style={{ width: "min(64vw, 280px)", height: "auto" }}
          fill="#FFFFFF"
          role="img"
          aria-label="E Service"
          shapeRendering="geometricPrecision"
        >
          <g data-part="e-group" style={{ transform: `translateX(${E_OFFSET}px)` }}>
            <polygon data-e="top" points={E_PARTS.top} style={{ opacity: 0 }} />
            <polygon data-e="mid" points={E_PARTS.mid} style={{ opacity: 0 }} />
            <polygon data-e="bot" points={E_PARTS.bottom} style={{ opacity: 0 }} />
          </g>
          <g data-part="service" style={{ opacity: 0 }}>
            {SERVICE_ITEMS.map(({ ch, x }) => (
              <g key={`${ch}-${x}`} transform={`translate(${x} 0)`}>
                {GLYPHS[ch]}
              </g>
            ))}
          </g>
        </svg>
      </div>
    </div>
  );
};

export default IntroAnimation;
